import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../../firebase/firebaseConfig';
import { useDirectPremiumCheck } from '../../hooks/useDirectPremiumCheck';
import usePublishedRoles from '../../hooks/usePublishedRoles';
import { fetchTrackMetaBatch } from '../../services/achievementsService';
import { loadRoadmapSnippetStatsByIds } from '../../services/profileAchievementStatsService';
import { assignPrimaryRole } from '../../services/roleAssignmentService';
import { loadPublishedTracks, loadTrackNodes } from '../../services/roadmapService';
import { getAllTrackIdsForRoleMastery, roleIncludesOrphanTrackSnippets, getExtraTrackIdsForRole } from '../../utils/roleTrackContribution';
import { computeTrackSnippetMastery } from '../Roadmap/RoadmapList';
import AchievementTrophyIcon from './AchievementTrophyIcon';
import MasteryBrainIcon from './MasteryBrainIcon';
import {
  formatLanguageDisplay,
  formatRoadmapTrackTitle,
  normalizeRoadmapLanguageKey,
  trackMatchesLanguage
} from '../../utils/roadmapDisplay';
import { SNIPPET_MASTERY_TOOLTIP } from '../../constants/snippetMasteryCopy';
import { userCanAccessRoadmapTrack } from '../../utils/roadmapSubscription';
import { trackRoleSwitched, trackRolePathTrackStarted } from '../../utils/analytics';
import {
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_GRAPH_NAV_FROM_PROFILE_STATE,
  ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY,
  ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY,
  ROADMAP_PATHS,
  ROADMAP_ROLE_ID_SEARCH_PARAM
} from '../Roadmap/roadmapPaths';
import IconRenderer from '../ui/IconRenderer';
import UserProfileListPanel from './UserProfileListPanel';
import { UserProfileRolesListSkeleton } from './UserProfilePanelSkeletons';
import useProfileListPagination from './useProfileListPagination';
import { ReactComponent as UserProfileRolesPanelSnippetIcon } from '../../Icons/deck-icon.svg';
import './UserProfileRolesPanel.css';

const USER_PROFILE_ROLES_PAGE_SIZE_STORAGE_KEY = 'flashcode-profile-roles-page-size';

function languageIconNameFromKey(langKey) {
  if (!langKey) return 'javascript';
  const k = String(langKey).trim().toLowerCase();
  if (k === 'c++' || k === 'cpp' || k === 'cplusplus') return 'cpp';
  if (k === 'c#' || k === 'csharp') return 'csharp';
  return k.replace(/\s+/g, '') || 'javascript';
}

function inferLanguageFromTrackId(trackId) {
  const id = String(trackId || '').trim().toLowerCase();
  if (!id) return '';
  return normalizeRoadmapLanguageKey(id.split('-')[0]);
}

function publishedTrackForId(trackId, publishedTracks) {
  const id = trackId != null ? String(trackId).trim() : '';
  if (!id) return null;
  const catalogTrack = (publishedTracks || []).find((t) => t.trackId === id);
  if (catalogTrack) return catalogTrack;
  const inferred = inferLanguageFromTrackId(id);
  return inferred ? { trackId: id, language: inferred } : { trackId: id };
}

function trackIdMatchesLanguageFilter(trackId, languageFilter, publishedTracks) {
  if (!languageFilter) return true;
  return trackMatchesLanguage(publishedTrackForId(trackId, publishedTracks), languageFilter);
}

function roleSupportsLanguage(role, languageFilter, publishedTracks, allRoles) {
  if (!languageFilter) return true;
  const key = normalizeRoadmapLanguageKey(languageFilter);
  if (!key) return true;

  if (Array.isArray(role.languages) && role.languages.length > 0) {
    if (role.languages.some((lang) => normalizeRoadmapLanguageKey(lang) === key)) {
      return true;
    }
  }

  if ((role.tracks || []).some((step) => trackIdMatchesLanguageFilter(step.trackId, languageFilter, publishedTracks))) {
    return true;
  }

  if (roleIncludesOrphanTrackSnippets(role)) {
    const orphanIds = getExtraTrackIdsForRole(role, publishedTracks, allRoles);
    return orphanIds.some((trackId) => trackIdMatchesLanguageFilter(trackId, languageFilter, publishedTracks));
  }

  return false;
}

async function loadRoleMasterySummary(userId, role, publishedTracks, roles, languageFilter = '') {
  let masteryTrackIds = getAllTrackIdsForRoleMastery(role, publishedTracks, roles);
  if (languageFilter) {
    masteryTrackIds = masteryTrackIds.filter((trackId) =>
      trackIdMatchesLanguageFilter(trackId, languageFilter, publishedTracks)
    );
  }
  const nodeEntries = await Promise.all(
    masteryTrackIds.map(async (trackId) => [trackId, await loadTrackNodes(trackId)])
  );
  const nodesByTrackId = new Map(nodeEntries);

  const snippetIdSet = new Set();
  nodeEntries.forEach(([, nodes]) => {
    (nodes || []).forEach((node) => {
      (node.snippetIds || []).forEach((id) => {
        if (id) snippetIdSet.add(id);
      });
    });
  });

  const snippetStatsById = userId
    ? await loadRoadmapSnippetStatsByIds(userId, Array.from(snippetIdSet))
    : {};

  let mastered = 0;
  let total = 0;
  masteryTrackIds.forEach((trackId) => {
    const { total: trackTotal, mastered: trackMastered } = computeTrackSnippetMastery(
      nodesByTrackId.get(trackId) || [],
      snippetStatsById
    );
    total += trackTotal;
    mastered += trackMastered;
  });

  return { mastered, total };
}

/**
 * Profile → Achievements → Roles: read-only role paths with SRS snippet mastery overlays.
 */
export default function UserProfileRolesPanel({
  userId,
  languageFilter = '',
  canView,
  primaryRoleId = null,
  onPrimaryRoleChange
}) {
  const navigate = useNavigate();
  const { roles, loading, error } = usePublishedRoles();
  const { isPremiumUser } = useDirectPremiumCheck(userId);
  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const [trackRowsByRole, setTrackRowsByRole] = useState({});
  const [roleMasteryByRole, setRoleMasteryByRole] = useState({});
  const [publishedTracks, setPublishedTracks] = useState([]);
  const [tracksLoadingRoleId, setTracksLoadingRoleId] = useState(null);
  const [masteryLoading, setMasteryLoading] = useState(false);
  const [switchingPrimaryRoleId, setSwitchingPrimaryRoleId] = useState(null);

  const isAnonymousGuest = Boolean(auth.currentUser?.isAnonymous);
  const showProgress = Boolean(userId && canView && !isAnonymousGuest);
  const canSetPrimaryRole = showProgress && typeof onPrimaryRoleChange === 'function';

  const sortedRoles = useMemo(() => {
    if (!primaryRoleId) return roles;
    const primary = roles.find((r) => r.roleId === primaryRoleId);
    const rest = roles.filter((r) => r.roleId !== primaryRoleId);
    return primary ? [primary, ...rest] : roles;
  }, [roles, primaryRoleId]);

  const visibleRoles = useMemo(() => {
    if (!languageFilter) return sortedRoles;
    return sortedRoles.filter((role) =>
      roleSupportsLanguage(role, languageFilter, publishedTracks, roles)
    );
  }, [sortedRoles, languageFilter, publishedTracks, roles]);

  const {
    pageSize,
    currentPage,
    setCurrentPage,
    totalPages,
    rangeStart,
    rangeEnd,
    paginate,
    handlePageSizeChange
  } = useProfileListPagination(visibleRoles.length, {
    storageKey: USER_PROFILE_ROLES_PAGE_SIZE_STORAGE_KEY,
    resetDeps: [languageFilter, userId]
  });

  const paginatedRoles = useMemo(() => paginate(visibleRoles), [visibleRoles, paginate]);

  useEffect(() => {
    setExpandedRoleId(null);
  }, [currentPage, languageFilter]);

  useEffect(() => {
    if (!languageFilter || !expandedRoleId) return;
    const role = roles.find((r) => r.roleId === expandedRoleId);
    if (role && !roleSupportsLanguage(role, languageFilter, publishedTracks, roles)) {
      setExpandedRoleId(null);
    }
  }, [languageFilter, expandedRoleId, roles, publishedTracks]);

  const handleSetPrimaryRole = useCallback(
    async (roleId, event) => {
      event?.stopPropagation();
      if (!canSetPrimaryRole || !userId || !roleId || roleId === primaryRoleId) return;
      if (switchingPrimaryRoleId) return;

      setSwitchingPrimaryRoleId(roleId);
      try {
        const previousRoleId = primaryRoleId;
        await assignPrimaryRole(userId, roleId);
        trackRoleSwitched({
          role_id: roleId,
          previous_role_id: previousRoleId || null,
          surface: 'profile-roles'
        });
        onPrimaryRoleChange?.(roleId);
      } catch (err) {
        console.error('UserProfileRolesPanel: set primary role failed', err);
      } finally {
        setSwitchingPrimaryRoleId(null);
      }
    },
    [canSetPrimaryRole, userId, primaryRoleId, switchingPrimaryRoleId, onPrimaryRoleChange]
  );

  const openTrackInLearn = useCallback(
    (roleId, step) => {
      if (!step?.trackId) return;
      const trackRef = {
        trackId: step.trackId,
        language: step.language,
        title: step.trackTitle
      };
      const canStudy = userCanAccessRoadmapTrack(isPremiumUser, step.trackId, trackRef);
      if (canStudy) {
        trackRolePathTrackStarted({
          role_id: roleId,
          track_id: step.trackId,
          surface: 'profile-roles'
        });
      }
      const langKey = normalizeRoadmapLanguageKey(step.language || 'javascript');
      const params = createSearchParams({
        [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey,
        [ROADMAP_ROLE_ID_SEARCH_PARAM]: roleId
      });
      navigate(
        {
          pathname: ROADMAP_PATHS.graph,
          search: params.toString()
        },
        {
          state: {
            ...ROADMAP_GRAPH_NAV_FROM_PROFILE_STATE,
            [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: step.trackId,
            [ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]: true
          }
        }
      );
    },
    [navigate, isPremiumUser]
  );

  const openSignup = useCallback(() => {
    navigate('/auth');
  }, [navigate]);

  const toggleRole = useCallback((roleId) => {
    setExpandedRoleId((prev) => (prev === roleId ? null : roleId));
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadPublishedTracks()
      .then((tracks) => {
        if (!cancelled) setPublishedTracks(tracks || []);
      })
      .catch(() => {
        if (!cancelled) setPublishedTracks([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setTrackRowsByRole({});
  }, [languageFilter]);

  useEffect(() => {
    if (!showProgress || !roles.length) {
      if (!showProgress) setRoleMasteryByRole({});
      return;
    }

    let cancelled = false;
    setMasteryLoading(true);

    (async () => {
      try {
        const entries = await Promise.all(
          roles.map(async (role) => {
            const summary = await loadRoleMasterySummary(
              userId,
              role,
              publishedTracks,
              roles,
              languageFilter
            );
            return [role.roleId, summary];
          })
        );
        if (!cancelled) {
          setRoleMasteryByRole(Object.fromEntries(entries));
        }
      } catch (e) {
        console.error('UserProfileRolesPanel: role mastery load failed', e);
      } finally {
        if (!cancelled) setMasteryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showProgress, userId, roles, publishedTracks, languageFilter]);

  useEffect(() => {
    if (!expandedRoleId) return;
    const role = roles.find((r) => r.roleId === expandedRoleId);
    if (!role?.tracks?.length) return;
    if (trackRowsByRole[expandedRoleId]) return;

    let cancelled = false;
    setTracksLoadingRoleId(expandedRoleId);

    (async () => {
      try {
        const displayTrackIds = role.tracks
          .map((t) => t.trackId)
          .filter((trackId) => trackIdMatchesLanguageFilter(trackId, languageFilter, publishedTracks));
        if (!displayTrackIds.length) {
          if (!cancelled) {
            setTrackRowsByRole((prev) => ({ ...prev, [expandedRoleId]: [] }));
            setTracksLoadingRoleId(null);
          }
          return;
        }
        const masteryTrackIds = getAllTrackIdsForRoleMastery(role, publishedTracks, roles);
        const nodeEntries = await Promise.all(
          masteryTrackIds.map(async (trackId) => [trackId, await loadTrackNodes(trackId)])
        );
        const nodesByTrackId = new Map(nodeEntries);

        const snippetIdSet = new Set();
        nodeEntries.forEach(([, nodes]) => {
          (nodes || []).forEach((node) => {
            (node.snippetIds || []).forEach((id) => {
              if (id) snippetIdSet.add(id);
            });
          });
        });

        const snippetStatsById =
          showProgress && userId
            ? await loadRoadmapSnippetStatsByIds(userId, Array.from(snippetIdSet))
            : {};

        const metaMap = await fetchTrackMetaBatch(displayTrackIds);
        const rows = role.tracks
          .filter((step) => trackIdMatchesLanguageFilter(step.trackId, languageFilter, publishedTracks))
          .map((step) => {
          const meta = metaMap.get(step.trackId) || { title: null, language: null };
          const nodes = nodesByTrackId.get(step.trackId) || [];
          const { total, mastered } = computeTrackSnippetMastery(
            nodes,
            showProgress ? snippetStatsById : {}
          );

          return {
            ...step,
            trackTitle: (meta.title && String(meta.title).trim()) || step.trackId,
            language: meta.language || 'other',
            snippetTotal: total,
            snippetMastered: mastered,
            isComplete: total > 0 && mastered >= total
          };
        });
        if (!cancelled) {
          setTrackRowsByRole((prev) => ({ ...prev, [expandedRoleId]: rows }));
        }
      } catch (e) {
        console.error('UserProfileRolesPanel: track load failed', e);
      } finally {
        if (!cancelled) setTracksLoadingRoleId(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [expandedRoleId, roles, publishedTracks, showProgress, trackRowsByRole, userId, languageFilter]);

  if (loading) {
    return (
      <UserProfileListPanel
        className="user-profile-roles-panel__list-panel"
        ariaLabel="Learning roles"
        loading
        loadingContent={<UserProfileRolesListSkeleton />}
      />
    );
  }

  if (error && roles.length === 0) {
    return (
      <UserProfileListPanel
        className="user-profile-roles-panel__list-panel"
        ariaLabel="Learning roles"
        empty
        emptyContent={
          <p className="user-profile-roles-panel__empty">
            Could not load roles. Try again later.
          </p>
        }
      />
    );
  }

  if (!roles.length) {
    return (
      <UserProfileListPanel
        className="user-profile-roles-panel__list-panel"
        ariaLabel="Learning roles"
        empty
        emptyContent={
          <p className="user-profile-roles-panel__empty">
            Role paths are syncing — check back soon.
          </p>
        }
      />
    );
  }

  const emptyLangLabel =
    languageFilter && languageFilter !== 'other'
      ? formatLanguageDisplay(languageFilter)
      : null;

  return (
    <div className="user-profile-roles-panel">
      {isAnonymousGuest ? (
        <div className="user-profile-roles-panel__guest-cta">
          <p className="user-profile-roles-panel__guest-cta-text">
            Sign up free to save progress on your role path.
          </p>
          <button type="button" className="user-profile-roles-panel__guest-cta-btn" onClick={openSignup}>
            Sign up
          </button>
        </div>
      ) : null}

      <UserProfileListPanel
        className="user-profile-roles-panel__list-panel"
        ariaLabel="Learning roles"
        empty={visibleRoles.length === 0}
        emptyContent={
          <p className="user-profile-roles-panel__empty">
            {emptyLangLabel ? `No roles for ${emptyLangLabel}.` : 'No roles match this language.'}
          </p>
        }
        itemCount={visibleRoles.length}
        pageSize={pageSize}
        currentPage={currentPage}
        totalPages={totalPages}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        onPageSizeChange={handlePageSizeChange}
        onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
        onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        pageSizeSelectId="user-profile-roles-panel-page-size"
        navAriaLabel="Role list pages"
        perPageAriaLabel="Roles per page"
      >
        <ul className="user-profile-roles-panel__list" aria-label="Learning roles">
          {paginatedRoles.map((role) => {
          const isPrimary = Boolean(primaryRoleId && role.roleId === primaryRoleId);
          const isExpanded = expandedRoleId === role.roleId;
          const trackRows = trackRowsByRole[role.roleId];
          const visibleTrackSteps = (trackRows || role.tracks || []).filter((step) =>
            trackIdMatchesLanguageFilter(step.trackId, languageFilter, publishedTracks)
          );
          const isTracksLoading = tracksLoadingRoleId === role.roleId;
          const mastery = roleMasteryByRole[role.roleId];

          return (
            <li
              key={role.roleId}
              className={[
                'user-profile-roles-panel__item',
                isPrimary ? 'user-profile-roles-panel__item--primary' : ''
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className="user-profile-roles-panel__card">
                <button
                  type="button"
                  className={`user-profile-roles-panel__card-header${isExpanded ? ' user-profile-roles-panel__card-header--expanded' : ''}`}
                  aria-expanded={isExpanded}
                  onClick={() => toggleRole(role.roleId)}
                >
                  <span className="user-profile-roles-panel__card-content">
                    <span className="user-profile-roles-panel__card-primary-row">
                      <span className="user-profile-roles-panel__card-title-line">
                        <span className="user-profile-roles-panel__card-icon-wrap" aria-hidden>
                          {role.iconClass ? (
                            <i className={`ti ${role.iconClass} user-profile-roles-panel__card-icon`} />
                          ) : (
                            <i className="ti ti-user user-profile-roles-panel__card-icon" />
                          )}
                        </span>
                        <span className="user-profile-roles-panel__card-title">{role.title}</span>
                      </span>
                      <span className="user-profile-roles-panel__card-aside">
                        {showProgress && (mastery?.total > 0 || masteryLoading) ? (
                          <div
                            className={[
                              'user-profile__achievements-stat-pill',
                              'user-profile__achievements-stat-pill--mastery',
                              'user-profile-roles-panel__role-mastery-stat',
                              !masteryLoading && mastery.mastered > 0
                                ? 'user-profile__achievements-stat-pill--mastery-has'
                                : ''
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            title={SNIPPET_MASTERY_TOOLTIP}
                            aria-label={
                              masteryLoading
                                ? 'Snippets mastered in this role, loading'
                                : `${mastery.mastered} of ${mastery.total} snippets mastered in ${role.title}`
                            }
                          >
                            <MasteryBrainIcon
                              className="user-profile__achievements-stat-pill__icon user-profile__achievements-stat-pill__icon--brain"
                              aria-hidden
                            />
                            <span className="user-profile__achievements-stat-pill__value">
                              {masteryLoading ? '-' : mastery.mastered}
                            </span>
                          </div>
                        ) : null}
                        <span className="user-profile-roles-panel__card-chevron" aria-hidden>
                          {isExpanded ? '▾' : '▸'}
                        </span>
                      </span>
                    </span>
                    {role.promise ? (
                      <span className="user-profile-roles-panel__card-promise">{role.promise}</span>
                    ) : null}
                  </span>
                </button>

                {isExpanded ? (
                  <div className="user-profile-roles-panel__tracks">
                    {!isPrimary && canSetPrimaryRole ? (
                      <button
                        type="button"
                        className="user-profile-roles-panel__set-primary-btn"
                        onClick={(event) => handleSetPrimaryRole(role.roleId, event)}
                        disabled={switchingPrimaryRoleId === role.roleId}
                      >
                        {switchingPrimaryRoleId === role.roleId ? 'Saving…' : 'Set as your path'}
                      </button>
                    ) : null}
                    {mastery?.total > 0 ? (
                      <div
                        className="user-profile-roles-panel__card-meta"
                        title={SNIPPET_MASTERY_TOOLTIP}
                        aria-label={`${mastery.total} snippets in ${role.title}`}
                      >
                        {role.includesOrphanTracks ? (
                          <span className="user-profile-roles-panel__card-meta-note">
                            Includes unmapped tracks
                          </span>
                        ) : null}
                        <span className="user-profile-roles-panel__card-meta-total">
                          <UserProfileRolesPanelSnippetIcon
                            className="user-profile-roles-panel__card-meta-snippet-icon"
                            aria-hidden
                          />
                          {mastery.total} snippets
                        </span>
                      </div>
                    ) : null}
                    {isTracksLoading ? (
                      <p className="user-profile-roles-panel__tracks-loading">Loading tracks…</p>
                    ) : null}
                    {!isTracksLoading && visibleTrackSteps.length === 0 ? (
                      <p className="user-profile-roles-panel__tracks-loading">
                        {languageFilter
                          ? `No tracks for ${formatLanguageDisplay(languageFilter)} in this role.`
                          : 'No tracks in this role yet.'}
                      </p>
                    ) : null}
                    <ol className="user-profile-roles-panel__track-list">
                      {visibleTrackSteps.map((step, index) => {
                        const langKey = normalizeRoadmapLanguageKey(step.language || 'other');
                        const trackTitle = formatRoadmapTrackTitle(langKey, step.trackTitle || step.trackId, {
                          language: step.language
                        });
                        const total = step.snippetTotal ?? 0;
                        const mastered = step.snippetMastered ?? 0;
                        const pct = total > 0 ? Math.round((mastered / total) * 100) : 0;
                        const showTrackProgress = showProgress && total > 0;
                        const isComplete = step.isComplete;
                        const trackRef = {
                          trackId: step.trackId,
                          language: step.language,
                          title: step.trackTitle
                        };
                        const isLocked = !userCanAccessRoadmapTrack(
                          isPremiumUser,
                          step.trackId,
                          trackRef
                        );

                        return (
                          <li key={step.entryId || step.trackId} className="user-profile-roles-panel__track-item">
                            <button
                              type="button"
                              className={[
                                'user-profile-roles-panel__track-row',
                                isComplete ? 'user-profile-roles-panel__track-row--complete' : '',
                                isLocked ? 'user-profile-roles-panel__track-row--locked' : ''
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() => openTrackInLearn(role.roleId, step)}
                              disabled={isTracksLoading || !trackRows}
                              aria-label={
                                isLocked
                                  ? `Preview ${trackTitle} (Pro track)`
                                  : `Open ${trackTitle} in Learn`
                              }
                            >
                              <span className="user-profile-roles-panel__track-order" aria-hidden>
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              <span className="user-profile-roles-panel__track-icon-wrap" aria-hidden>
                                <IconRenderer
                                  name={languageIconNameFromKey(langKey)}
                                  type="language"
                                  size="small"
                                  className="user-profile-roles-panel__track-lang-icon"
                                />
                              </span>
                              <span className="user-profile-roles-panel__track-title-line">
                                <span className="user-profile-roles-panel__track-title">{trackTitle}</span>
                                {isComplete ? (
                                  <AchievementTrophyIcon
                                    className="user-profile-roles-panel__track-trophy"
                                    aria-label="All snippets mastered in this track"
                                  />
                                ) : null}
                              </span>
                              {showTrackProgress ? (
                                <div className="user-profile-roles-panel__track-progress">
                                  <div
                                    className="user-profile-roles-panel__track-progress-track"
                                    role="progressbar"
                                    aria-valuenow={mastered}
                                    aria-valuemin={0}
                                    aria-valuemax={total}
                                    aria-label={`${mastered} of ${total} snippets mastered`}
                                  >
                                    <div
                                      className={[
                                        'user-profile-roles-panel__track-progress-fill',
                                        isComplete
                                          ? 'user-profile-roles-panel__track-progress-fill--complete'
                                          : ''
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="user-profile-roles-panel__track-progress-label">
                                    {mastered}/{total}
                                  </span>
                                </div>
                              ) : null}
                              {isLocked ? (
                                <span
                                  className="user-profile-roles-panel__track-pro-label"
                                  title="Pro track — preview cookbooks and snippets; subscribe to study"
                                >
                                  Pro
                                </span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
        </ul>
      </UserProfileListPanel>
    </div>
  );
}
